'use client';

import { Spinner } from '@shared/icons';
import { twMerge } from '@shared/themes';
import { useTranslations } from 'next-intl';
import type { ComponentProps } from 'react';
import { BorderContainer } from '../../../BorderContainer/BorderContainer';
import { CountdownTwentyFourHours } from '../../../CountdownTwentyFourHours/CountdownTwentyFourHours';
import type { ResultsDrawStatus } from '../../utils';

type LotteryDrawStatusProps = ComponentProps<typeof BorderContainer> & {
  status: Exclude<ResultsDrawStatus, 'numbers'>;
  closingDate: number;
  drawingDate: number;
};

const classes = {
  status: 'mx-auto flex items-center justify-center gap-x-1.5 px-5 py-2 text-center paragraph-s-strong xs:paragraph-l-strong text-foreground',
};

/**
 * LotteryDrawStatus displays the current results-page draw status inside a BorderContainer.
 *
 * It shows a translated status label and, except during a live draw, a countdown to closing/drawing.
 * While results are still arriving it shows a spinner, with the status label kept for screen readers only.
 *
 * @param {LotteryDrawStatusProps} props - The props for the LotteryDrawStatus component.
 * @param {'draw_closes_in' | 'live_draw_in' | 'live_draw_in_progress' | 'awaiting_results'} props.status - The draw status to display.
 * @param {number} props.closingDate - The closing timestamp used by the countdown.
 * @param {number} props.drawingDate - The drawing timestamp used by the countdown.
 * @returns {JSX.Element} The rendered LotteryDrawStatus component.
 */
function LotteryDrawStatus({ status, closingDate, drawingDate, className, ...props }: LotteryDrawStatusProps) {
  const t = useTranslations('lotteries.results');

  const timestamp = status === 'live_draw_in' ? drawingDate : closingDate;
  const showCountdown = status === 'draw_closes_in' || status === 'live_draw_in';

  return (
    <BorderContainer {...props} className={twMerge('w-full', className)}>
      <div className={classes.status}>
        {status === 'awaiting_results' ? (
          <span role="status" aria-live="polite">
            <Spinner size="regular" className="animate-spin" aria-hidden />
            <span className="sr-only">{t(status)}</span>
          </span>
        ) : (
          <span>{t(status)}</span>
        )}
        {showCountdown && <CountdownTwentyFourHours timestamp={timestamp} letterWidth="w-2.25" />}
      </div>
    </BorderContainer>
  );
}

export { LotteryDrawStatus };
